import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import api from '../../lib/api';

const pricingTypeOptions = [
  { value: 0, label: 'Фиксированная стоимость' },
  { value: 1, label: 'За час' },
  { value: 2, label: 'За день' },
];

const itemTypeOptions = [
  { value: 0, label: 'Привязана к бронированию площадки' },
  { value: 1, label: 'Независимая услуга (прокат)' },
];

class RentalItemsManager extends Component {
  static propTypes = {
    complexId: PropTypes.number.isRequired,
    playgrounds: PropTypes.array.isRequired,
    classes: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      items: [],
      loading: false,
      dialogOpen: false,
      editingItem: null,
      formData: {
        name: '',
        description: '',
        price: '',
        pricingType: 0,
        itemType: 0,
        quantity: '',
        playgroundIds: [],
      },
    };
  }

  componentDidMount() {
    if (this.props.complexId) {
      this.loadItems();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.complexId !== this.props.complexId && this.props.complexId) {
      this.loadItems();
    }
  }

  loadItems = async () => {
    try {
      this.setState({ loading: true });
      const items = await api.getRentalItems(this.props.complexId);
      this.setState({ items: items || [] });
    } catch (error) {
      console.error('Ошибка загрузки услуг:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  openDialog = (item = null) => {
    if (item) {
      // Преобразуем строковые значения enum в числа, если они пришли как строки
      let pricingType = item.pricingType;
      if (typeof pricingType === 'string') {
        if (pricingType === 'Fixed' || pricingType === 'fixed') {
          pricingType = 0;
        } else if (pricingType === 'PerHour' || pricingType === 'per_hour' || pricingType === 'PerHour') {
          pricingType = 1;
        } else if (pricingType === 'PerDay' || pricingType === 'per_day') {
          pricingType = 2;
        } else {
          pricingType = parseInt(pricingType, 10) || 0;
        }
      } else {
        pricingType = pricingType || 0;
      }

      let itemType = item.itemType;
      if (typeof itemType === 'string') {
        if (itemType === 'AttachedToBooking' || itemType === 'attached_to_booking') {
          itemType = 0;
        } else if (itemType === 'Standalone' || itemType === 'standalone') {
          itemType = 1;
        } else {
          itemType = parseInt(itemType, 10) || 0;
        }
      } else {
        itemType = itemType || 0;
      }

      this.setState({
        editingItem: item,
        formData: {
          name: item.name || '',
          description: item.description || '',
          price: item.price || '',
          pricingType: pricingType,
          itemType: itemType,
          quantity: item.quantity || '',
          playgroundIds: item.playgroundIds || [],
        },
        dialogOpen: true,
      });
    } else {
      this.setState({
        editingItem: null,
        formData: {
          name: '',
          description: '',
          price: '',
          pricingType: 0,
          itemType: 0,
          quantity: '',
          playgroundIds: [],
        },
        dialogOpen: true,
      });
    }
  };

  closeDialog = () => {
    this.setState({
      dialogOpen: false,
      editingItem: null,
      formData: {
        name: '',
        description: '',
        price: '',
        pricingType: 0,
        itemType: 0,
        quantity: '',
        playgroundIds: [],
      },
    });
  };

  handleFormChange = (field, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value,
      },
    }));
  };

  handlePlaygroundToggle = (playgroundId) => {
    this.setState(prevState => {
      const playgroundIds = [...prevState.formData.playgroundIds];
      const index = playgroundIds.indexOf(playgroundId);
      
      if (index > -1) {
        playgroundIds.splice(index, 1);
      } else {
        playgroundIds.push(playgroundId);
      }
      
      return {
        formData: {
          ...prevState.formData,
          playgroundIds,
        },
      };
    });
  };

  handleSubmit = async () => {
    const { formData, editingItem } = this.state;
    const { complexId } = this.props;

    if (!formData.name || !formData.price) {
      alert('Заполните название и стоимость'); // eslint-disable-line
      return;
    }

    try {
      const requestData = {
        sportComplexId: complexId,
        name: formData.name,
        description: formData.description || '',
        price: parseFloat(formData.price) || 0,
        pricingType: formData.pricingType,
        itemType: formData.itemType,
        quantity: formData.itemType === 1 && formData.quantity ? parseInt(formData.quantity, 10) : null,
        playgroundIds: formData.playgroundIds.length > 0 ? formData.playgroundIds : null,
      };

      if (editingItem) {
        await api.updateRentalItem(editingItem.id, requestData);
      } else {
        await api.createRentalItem(requestData);
      }

      this.closeDialog();
      this.loadItems();
    } catch (error) {
      console.error('Ошибка сохранения услуги:', error);
      alert('Ошибка сохранения услуги'); // eslint-disable-line
    }
  };

  handleDelete = async (itemId) => {
    if (!confirm('Вы действительно хотите удалить эту услугу?')) { // eslint-disable-line
      return;
    }

    try {
      await api.deleteRentalItem(itemId);
      this.loadItems();
    } catch (error) {
      console.error('Ошибка удаления услуги:', error);
      alert('Ошибка удаления услуги'); // eslint-disable-line
    }
  };

  getPricingTypeLabel = (type) => {
    // Обрабатываем как числовые значения, так и строковые enum'ы из бэкенда
    let numericValue = type;
    if (typeof type === 'string') {
      // Преобразуем строковые enum'ы в числовые значения
      const enumMap = {
        'Fixed': 0,
        'PerHour': 1,
        'PerDay': 2,
        '0': 0,
        '1': 1,
        '2': 2,
      };
      numericValue = enumMap[type] !== undefined ? enumMap[type] : parseInt(type, 10);
    }
    const option = pricingTypeOptions.find(opt => opt.value === numericValue);
    return option ? option.label : 'Неизвестно';
  };

  getItemTypeLabel = (type) => {
    // Обрабатываем как числовые значения, так и строковые enum'ы из бэкенда
    let numericValue = type;
    if (typeof type === 'string') {
      // Преобразуем строковые enum'ы в числовые значения
      const enumMap = {
        'AttachedToBooking': 0,
        'Standalone': 1,
        '0': 0,
        '1': 1,
      };
      numericValue = enumMap[type] !== undefined ? enumMap[type] : parseInt(type, 10);
    }
    const option = itemTypeOptions.find(opt => opt.value === numericValue);
    return option ? option.label : 'Неизвестно';
  };

  render() {
    const { classes, playgrounds } = this.props;
    const { items, loading, dialogOpen, formData, editingItem } = this.state;

    const playgroundOptions = playgrounds
      .filter(pg => pg.complexId === this.props.complexId)
      .map(pg => ({
        value: pg.id,
        label: pg.name,
      }));

    return (
      <Paper className={classes.paperImages} elevation={2}>
        <div className={classes.header}>
          <div>
            <Typography variant="h5" className={classes.title}>Услуги и инвентарь</Typography>
            <Typography variant="caption" className={classes.subtitle}>
              Управление дополнительными услугами и инвентарем для площадок комплекса
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => this.openDialog()}
            className={classes.button}
          >
            Добавить услугу
          </Button>
        </div>

        {loading ? (
          <Typography>Загрузка...</Typography>
        ) : items.length === 0 ? (
          <Typography style={{ color: '#666', fontStyle: 'italic' }}>
            Услуги не добавлены. Нажмите "Добавить услугу" для создания.
          </Typography>
        ) : (
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Стоимость</TableCell>
                <TableCell>Тарификация</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Площадки</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.price} руб.</TableCell>
                  <TableCell>{this.getPricingTypeLabel(item.pricingType)}</TableCell>
                  <TableCell>{this.getItemTypeLabel(item.itemType)}</TableCell>
                  <TableCell>
                    {item.playgroundIds && item.playgroundIds.length > 0
                      ? `${item.playgroundIds.length} площадок`
                      : 'Все площадки'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => this.openDialog(item)}
                      style={{ marginRight: 8 }}
                      aria-label="Редактировать"
                    >
                      <span role="img" aria-label="Редактировать">✏️</span>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => this.handleDelete(item.id)}
                      aria-label="Удалить"
                    >
                      <span role="img" aria-label="Удалить">🗑️</span>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={dialogOpen}
          onClose={this.closeDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingItem ? 'Редактирование услуги' : 'Добавление услуги'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Название услуги"
              value={formData.name}
              onChange={(e) => this.handleFormChange('name', e.target.value)}
              margin="normal"
              required
              className={classes.field}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Описание"
              value={formData.description}
              onChange={(e) => this.handleFormChange('description', e.target.value)}
              margin="normal"
              multiline
              rows={3}
              className={classes.field}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Стоимость"
              type="number"
              value={formData.price}
              onChange={(e) => this.handleFormChange('price', e.target.value)}
              margin="normal"
              required
              inputProps={{ min: 0, step: 0.01 }}
              className={classes.field}
              variant="outlined"
            />
            <FormControl component="fieldset" margin="normal" fullWidth>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Метод тарификации</Typography>
              <RadioGroup
                value={String(formData.pricingType || 0)}
                onChange={(e) => this.handleFormChange('pricingType', parseInt(e.target.value, 10))}
              >
                {pricingTypeOptions.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value.toString()}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Тип услуги</Typography>
              <RadioGroup
                value={String(formData.itemType || 0)}
                onChange={(e) => this.handleFormChange('itemType', parseInt(e.target.value, 10))}
              >
                {itemTypeOptions.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value.toString()}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {formData.itemType === 1 && (
              <TextField
                fullWidth
                label="Количество единиц"
                type="number"
                className={classes.field}
                variant="outlined"
                value={formData.quantity}
                onChange={(e) => this.handleFormChange('quantity', e.target.value)}
                margin="normal"
                inputProps={{ min: 1 }}
                helperText="Для независимых услуг (например, 20 сноубордов)"
              />
            )}
            <FormControl component="fieldset" margin="normal" fullWidth>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Доступна на площадках (оставьте пустым для всех площадок)
              </Typography>
              <FormGroup>
                {playgroundOptions.map(option => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={formData.playgroundIds.includes(option.value)}
                        onChange={() => this.handlePlaygroundToggle(option.value)}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} className={classes.button} variant="outlined">Отмена</Button>
            <Button onClick={this.handleSubmit} color="primary" variant="contained" className={classes.button}>
              {editingItem ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  }
}

const styles = (theme) => ({
  paperImages: {
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    alignItems: 'stretch',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.3s ease',
    boxSizing: 'border-box',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
  },
  table: {
    '& thead th': {
      backgroundColor: '#f8f9fa',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#495057',
      borderBottom: '2px solid #dee2e6',
    },
    '& tbody tr': {
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
    },
    '& tbody td': {
      borderBottom: '1px solid #e9ecef',
      padding: theme.spacing(1.5),
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    fontSize: '1.25rem',
    color: '#212529',
  },
  subtitle: {
    display: 'block',
    marginTop: theme.spacing(1),
    color: '#666',
    fontSize: '0.875rem',
  },
  button: {
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  field: {
    marginTop: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1976d2',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
      },
    },
  },
});

export default withStyles(styles)(RentalItemsManager);

